import { QRCodeSVG } from 'qrcode.react'
import { useEffect, useState } from 'react'

import { UserRole } from '@/api/admin-types'
import {
	useCreatePointOfSaleGmsEnrollment,
	useDeletePointOfSaleGmsDevice,
	useEnrollPointOfSaleDevice,
	useNotifyPointOfSaleAppUpdate,
	useRebootPointOfSaleGmsDevice,
	useLockPointOfSaleKiosk,
	useRefreshPointOfSaleDeviceStatus,
	useRestartPointOfSaleApp,
	useSyncPointOfSaleGmsPolicy,
	useUnenrollPointOfSaleDevice
} from '@/api/api-mutations'
import { useApiContext, useGetGmsPinnableVersions, useGetMe, useGetPointOfSaleDeviceStatus } from '@/api/react-query'
import { Badge } from '@/components/base/badge'
import { Button } from '@/components/base/button'
import { Container, ContainerHeader } from '@/components/base/container'
import { Drawer } from '@/components/base/drawer'
import { EmptyState } from '@/components/base/empty-state'
import { Heading } from '@/components/base/heading'
import { usePrompt } from '@/components/base/prompt'
import { SectionRow } from '@/components/base/section-row'
import { Select } from '@/components/base/select'
import { StatusBadge } from '@/components/base/status-badge'
import { Text } from '@/components/base/text'
import { Textarea } from '@/components/base/textarea'
import { toast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, useAppForm } from '@/components/form/form'
import { useI18n } from '@/i18n/i18n-react'
import { isAdminOrDeveloperRole } from '@/util/invite-role-label'

// Live-ish presence without a websocket subscription in the dashboard itself — good enough for
// an admin glancing at a single device, and cheap since it's scoped to one point of sale.
const DEVICE_STATUS_REFETCH_INTERVAL_MS = 15_000

const UNPINNED_VALUE = '__UNPINNED__'

function isDeviceOffline(response: unknown): boolean {
	if (!response || typeof response !== 'object' || !('errorCode' in response)) {
		return false
	}
	return response.errorCode === 'DEVICE_OFFLINE'
}

function HealthBadge({ label, connected }: { label: string; connected?: boolean }) {
	const { L } = useI18n()
	// Match color to the label: anything not explicitly connected is treated as disconnected.
	// (Scanner/payment used to land as `undefined` and render grey while still saying Disconnected.)
	const isConnected = connected === true
	return (
		<StatusBadge color={isConnected ? 'green' : 'red'}>
			{label}: {isConnected ? L.Settings.DeviceStatus.Connected() : L.Settings.DeviceStatus.Disconnected()}
		</StatusBadge>
	)
}

function PushUpdateDrawer({
	pointOfSaleUuid,
	open,
	onOpenChange
}: {
	pointOfSaleUuid: string
	open: boolean
	onOpenChange: (open: boolean) => void
}) {
	const { L } = useI18n()
	const notifyAppUpdate = useNotifyPointOfSaleAppUpdate()

	const form = useAppForm({
		defaultValues: { channel: '' },
		onSubmit: async ({ value }) => {
			const result = await notifyAppUpdate({
				path: { pointOfSaleUuid },
				body: { channel: value.channel || undefined }
			})
			if (!result.isSuccess) {
				return
			}
			toast.success(L.Settings.DeviceStatus.PushUpdateSuccess())
			onOpenChange(false)
			form.reset()
		}
	})

	return (
		<Drawer open={open} onOpenChange={onOpenChange}>
			<Drawer.Content>
				<form.AppForm>
					<Drawer.Header>
						<Drawer.Title asChild>
							<Heading>{L.Settings.DeviceStatus.PushUpdateDrawer.Title()}</Heading>
						</Drawer.Title>
						<Drawer.Description>
							{L.Settings.DeviceStatus.PushUpdateDrawer.Description()}
						</Drawer.Description>
					</Drawer.Header>
					<Drawer.Body>
						<FormContainer>
							<FormFieldGroup>
								<form.AppField
									name='channel'
									children={field => (
										<field.TextField
											label={L.Settings.DeviceStatus.PushUpdateDrawer.ChannelLabel()}
											placeholder={L.Settings.DeviceStatus.PushUpdateDrawer.ChannelPlaceholder()}
										/>
									)}
								/>
							</FormFieldGroup>
						</FormContainer>
					</Drawer.Body>
					<Drawer.Footer>
						<Button variant='secondary' size='small' onClick={() => onOpenChange(false)}>
							{L.General.Cancel()}
						</Button>
						<form.SaveButton label={L.Settings.DeviceStatus.PushUpdateDrawer.Submit()} />
					</Drawer.Footer>
				</form.AppForm>
			</Drawer.Content>
		</Drawer>
	)
}

type DeviceInfo = NonNullable<
	NonNullable<ReturnType<typeof useGetPointOfSaleDeviceStatus>['data']>['enrollment']
>['deviceInfo']

const formatDeviceLabel = (deviceInfo: DeviceInfo): string =>
	[deviceInfo.deviceModel, [deviceInfo.platform, deviceInfo.platformVersion].filter(Boolean).join(' ')]
		.filter(Boolean)
		.join(' — ')

function EnrollDeviceDrawer({
	pointOfSaleUuid,
	open,
	onOpenChange
}: {
	pointOfSaleUuid: string
	open: boolean
	onOpenChange: (open: boolean) => void
}) {
	const { L } = useI18n()
	const { api } = useApiContext()
	const enrollDevice = useEnrollPointOfSaleDevice(true)
	// Confirm inline in the drawer — `usePrompt` mounts a separate AlertDialog root that can't
	// steal focus from an open Radix Drawer (Dialog), so the old flow spun forever on submit.
	const [pending, setPending] = useState<{
		code: string
		deviceInfo: DeviceInfo
		currentDevice: { deviceInfo: DeviceInfo } | null
	} | null>(null)

	const form = useAppForm({
		defaultValues: { code: '' },
		onSubmit: async ({ value }) => {
			const code = value.code.replace(/\s/g, '')

			const lookup = await api.lookupDeviceEnrollmentByCodeNoThrow({ path: { pointOfSaleUuid, code } })
			if (!lookup.isSuccess) {
				toast.error(L.Settings.DeviceStatus.Enrollment.EnrollDrawer.LookupError())
				return
			}

			const { deviceInfo, currentDevice } = lookup.response
			setPending({ code, deviceInfo, currentDevice: currentDevice ?? null })
		}
	})

	const handleClose = (nextOpen: boolean) => {
		onOpenChange(nextOpen)
		if (!nextOpen) {
			setPending(null)
			form.reset()
		}
	}

	const handleConfirmEnroll = async () => {
		if (!pending) {
			return
		}
		const result = await enrollDevice.mutateAsync({
			path: { pointOfSaleUuid },
			body: { code: pending.code }
		})
		if (result.isSuccess) {
			toast.success(L.Settings.DeviceStatus.Enrollment.EnrollDrawer.EnrollSuccess())
			handleClose(false)
		}
	}

	return (
		<Drawer open={open} onOpenChange={handleClose}>
			<Drawer.Content>
				{pending ? (
					<>
						<Drawer.Header>
							<Drawer.Title asChild>
								<Heading>{L.Settings.DeviceStatus.Enrollment.EnrollDrawer.ConfirmTitle()}</Heading>
							</Drawer.Title>
						</Drawer.Header>
						<Drawer.Body>
							<Drawer.Description>
								{pending.currentDevice
									? L.Settings.DeviceStatus.Enrollment.EnrollDrawer.ConfirmDescriptionWithCurrent({
											device: formatDeviceLabel(pending.deviceInfo),
											currentDevice: formatDeviceLabel(pending.currentDevice.deviceInfo)
										})
									: L.Settings.DeviceStatus.Enrollment.EnrollDrawer.ConfirmDescription({
											device: formatDeviceLabel(pending.deviceInfo)
										})}
							</Drawer.Description>
						</Drawer.Body>
						<Drawer.Footer>
							<Button variant='secondary' size='small' onClick={() => setPending(null)}>
								{L.General.Cancel()}
							</Button>
							<Button size='small' isLoading={enrollDevice.isPending} onClick={handleConfirmEnroll}>
								{L.Settings.DeviceStatus.Enrollment.EnrollDrawer.Submit()}
							</Button>
						</Drawer.Footer>
					</>
				) : (
					<form.AppForm>
						<Drawer.Header>
							<Drawer.Title asChild>
								<Heading>{L.Settings.DeviceStatus.Enrollment.EnrollDrawer.Title()}</Heading>
							</Drawer.Title>
							<Drawer.Description>
								{L.Settings.DeviceStatus.Enrollment.EnrollDrawer.Description()}
							</Drawer.Description>
						</Drawer.Header>
						<Drawer.Body>
							<FormContainer>
								<FormFieldGroup>
									<form.AppField
										name='code'
										validators={{
											onChange: ({ value }) => (!value ? L.General.Required() : undefined)
										}}
										children={field => (
											<field.TextField
												autoFocus
												maxLength={9}
												onKeyDown={e => {
													if (e.key === 'Enter') {
														e.preventDefault()
														void form.handleSubmit()
													}
												}}
												label={L.Settings.DeviceStatus.Enrollment.EnrollDrawer.CodeLabel()}
												placeholder={L.Settings.DeviceStatus.Enrollment.EnrollDrawer.CodePlaceholder()}
											/>
										)}
									/>
								</FormFieldGroup>
							</FormContainer>
						</Drawer.Body>
						<Drawer.Footer>
							<Button variant='secondary' size='small' onClick={() => handleClose(false)}>
								{L.General.Cancel()}
							</Button>
							<form.SaveButton label={L.Settings.DeviceStatus.Enrollment.EnrollDrawer.Submit()} />
						</Drawer.Footer>
					</form.AppForm>
				)}
			</Drawer.Content>
		</Drawer>
	)
}

function EnrollmentSection({
	pointOfSaleUuid,
	pointOfSaleName,
	enrollment
}: {
	pointOfSaleUuid: string
	pointOfSaleName: string
	enrollment: NonNullable<ReturnType<typeof useGetPointOfSaleDeviceStatus>['data']>['enrollment']
}) {
	const { L } = useI18n()
	const prompt = usePrompt()
	const [enrollOpen, setEnrollOpen] = useState(false)
	const unenrollDevice = useUnenrollPointOfSaleDevice(true)

	const handleRevoke = async () => {
		const confirmed = await prompt({
			variant: 'danger',
			title: L.Settings.DeviceStatus.Enrollment.RevokeConfirmTitle(),
			description: L.Settings.DeviceStatus.Enrollment.RevokeConfirmDescription(),
			verificationText: pointOfSaleName
		})
		if (!confirmed) {
			return
		}
		const result = await unenrollDevice.mutateAsync({ path: { pointOfSaleUuid } })
		if (!result.isSuccess) {
			return
		}
		toast.success(L.Settings.DeviceStatus.Enrollment.RevokeSuccess())
	}

	return (
		<>
			<ContainerHeader
				heading={L.Settings.DeviceStatus.Enrollment.Title()}
				rightSide={
					<div className='flex items-center gap-x-2'>
						{!!enrollment && (
							<Button
								size='small'
								variant='danger'
								isLoading={unenrollDevice.isPending}
								onClick={handleRevoke}
							>
								{L.Settings.DeviceStatus.Enrollment.RevokeButton()}
							</Button>
						)}
						<Button size='small' variant='secondary' onClick={() => setEnrollOpen(true)}>
							{L.Settings.DeviceStatus.Enrollment.EnrollButton()}
						</Button>
					</div>
				}
			/>

			{!enrollment ? (
				<div className='px-6 pb-6'>
					<Text size='small' className='text-ui-fg-subtle'>
						{L.Settings.DeviceStatus.Enrollment.NotEnrolled()}
					</Text>
				</div>
			) : (
				<>
					<SectionRow
						title={L.Settings.DeviceStatus.Enrollment.DeviceModel()}
						value={enrollment.deviceInfo.deviceModel}
					/>
					<SectionRow
						title={L.Settings.DeviceStatus.Enrollment.Platform()}
						value={[enrollment.deviceInfo.platform, enrollment.deviceInfo.platformVersion]
							.filter(Boolean)
							.join(' ')}
					/>
					<SectionRow
						title={L.Settings.DeviceStatus.Enrollment.AppVersion()}
						value={enrollment.deviceInfo.appVersion}
					/>
					<SectionRow
						title={L.Settings.DeviceStatus.Enrollment.EnrolledAt()}
						value={L.Date.DateAndTime(enrollment.enrolledAt)}
					/>
					<SectionRow
						title={L.Settings.DeviceStatus.Enrollment.Key()}
						value={
							<Text size='small' leading='compact' className='font-mono'>
								{enrollment.redacted}
							</Text>
						}
					/>
				</>
			)}

			<EnrollDeviceDrawer pointOfSaleUuid={pointOfSaleUuid} open={enrollOpen} onOpenChange={setEnrollOpen} />
		</>
	)
}

function GmsEnrollDrawer({
	pointOfSaleUuid,
	open,
	onOpenChange
}: {
	pointOfSaleUuid: string
	open: boolean
	onOpenChange: (open: boolean) => void
}) {
	const { L } = useI18n()
	const createEnrollment = useCreatePointOfSaleGmsEnrollment()
	const [enrollment, setEnrollment] = useState<{ qrCode: string; expiresAt: string } | undefined>()

	const form = useAppForm({
		defaultValues: { wifiSsid: '', wifiPassword: '' },
		onSubmit: async ({ value }) => {
			const result = await createEnrollment({
				path: { pointOfSaleUuid },
				body: {
					wifiSsid: value.wifiSsid || undefined,
					wifiPassword: value.wifiPassword || undefined
				}
			})
			if (result.isSuccess) {
				setEnrollment(result.response)
				toast.success(L.Settings.DeviceStatus.Gms.EnrollSuccess())
			}
		}
	})

	const handleClose = (nextOpen: boolean) => {
		onOpenChange(nextOpen)
		if (!nextOpen) {
			setEnrollment(undefined)
			form.reset()
		}
	}

	return (
		<Drawer open={open} onOpenChange={handleClose}>
			<Drawer.Content>
				<Drawer.Header>
					<Drawer.Title asChild>
						<Heading>{L.Settings.DeviceStatus.Gms.EnrollDrawer.Title()}</Heading>
					</Drawer.Title>
					<Drawer.Description>{L.Settings.DeviceStatus.Gms.EnrollDrawer.Description()}</Drawer.Description>
				</Drawer.Header>
				{enrollment ? (
					<>
						<Drawer.Body className='flex flex-col items-center gap-y-4 py-6'>
							<QRCodeSVG value={enrollment.qrCode} size={220} />
							<Text className='text-sm text-ui-fg-subtle'>
								{L.Settings.DeviceStatus.Gms.EnrollDrawer.QrExpiresAt({
									time: L.Date.DateAndTime(new Date(enrollment.expiresAt))
								})}
							</Text>
						</Drawer.Body>
						<Drawer.Footer>
							<Button size='small' onClick={() => handleClose(false)}>
								{L.Settings.DeviceStatus.Gms.EnrollDrawer.Done()}
							</Button>
						</Drawer.Footer>
					</>
				) : (
					<form.AppForm>
						<Drawer.Body>
							<FormContainer>
								<FormFieldGroup>
									<form.AppField
										name='wifiSsid'
										children={field => (
											<field.TextField
												label={L.Settings.DeviceStatus.Gms.EnrollDrawer.WifiSsidLabel()}
											/>
										)}
									/>
								</FormFieldGroup>
								<FormFieldGroup>
									<form.AppField
										name='wifiPassword'
										children={field => (
											<field.TextField
												type='password'
												label={L.Settings.DeviceStatus.Gms.EnrollDrawer.WifiPasswordLabel()}
											/>
										)}
									/>
								</FormFieldGroup>
							</FormContainer>
						</Drawer.Body>
						<Drawer.Footer>
							<Button variant='secondary' size='small' onClick={() => handleClose(false)}>
								{L.General.Cancel()}
							</Button>
							<form.SaveButton label={L.Settings.DeviceStatus.Gms.EnrollDrawer.Submit()} />
						</Drawer.Footer>
					</form.AppForm>
				)}
			</Drawer.Content>
		</Drawer>
	)
}

function GmsMinimumVersionRow({
	pointOfSaleUuid,
	minimumVersionCode
}: {
	pointOfSaleUuid: string
	minimumVersionCode?: number | null
}) {
	const { L } = useI18n()
	const versions = useGetGmsPinnableVersions()
	const syncPolicy = useSyncPointOfSaleGmsPolicy(true)
	const currentValue = minimumVersionCode ? String(minimumVersionCode) : UNPINNED_VALUE
	const [selected, setSelected] = useState(currentValue)

	useEffect(() => {
		setSelected(currentValue)
	}, [currentValue])

	const handleApply = async () => {
		const result = await syncPolicy.mutateAsync({
			path: { pointOfSaleUuid },
			body: { minimumVersionCode: selected === UNPINNED_VALUE ? null : Number(selected) }
		})
		if (!result.isSuccess) {
			return
		}
		toast.success(L.Settings.DeviceStatus.Gms.ApplyVersionSuccess())
	}

	return (
		<SectionRow
			title={L.Settings.DeviceStatus.Gms.MinimumVersionCode()}
			value={
				<div className='flex items-center gap-x-2'>
					<Select size='small' value={selected} onValueChange={setSelected}>
						<Select.Trigger className='w-[260px]'>
							<Select.Value />
						</Select.Trigger>
						<Select.Content>
							<Select.Item value={UNPINNED_VALUE}>
								{L.Settings.DeviceStatus.Gms.VersionUnpinned()}
							</Select.Item>
							{(versions.data ?? []).map(version => (
								<Select.Item key={version.versionCode} value={String(version.versionCode)}>
									{version.versionCode} ({version.runtimeVersion}
									{version.channel ? `, ${version.channel}` : ''})
								</Select.Item>
							))}
						</Select.Content>
					</Select>
					<Button
						size='small'
						variant='secondary'
						disabled={selected === currentValue}
						isLoading={syncPolicy.isPending}
						onClick={handleApply}
					>
						{L.Settings.DeviceStatus.Gms.ApplyVersionButton()}
					</Button>
				</div>
			}
		/>
	)
}

function GmsPolicyOverridesEditor({
	pointOfSaleUuid,
	policyOverrides
}: {
	pointOfSaleUuid: string
	policyOverrides?: unknown
}) {
	const { L } = useI18n()
	const syncPolicy = useSyncPointOfSaleGmsPolicy()
	const originalValue = JSON.stringify(policyOverrides ?? {}, null, 2)

	const form = useAppForm({
		defaultValues: { json: originalValue },
		onSubmit: async ({ value }) => {
			let parsed: unknown
			try {
				parsed = JSON.parse(value.json)
			} catch {
				toast.error(L.Settings.DeviceStatus.Gms.PolicyOverridesInvalidJson())
				return
			}
			const result = await syncPolicy({ path: { pointOfSaleUuid }, body: { policyOverrides: parsed } })
			if (!result.isSuccess) {
				return
			}
			toast.success(L.Settings.DeviceStatus.Gms.SaveOverridesSuccess())
		}
	})

	// Keep the editor in sync with refreshed `policyOverrides` (e.g. the 15s status poll) — without
	// this, an unedited editor would keep showing whatever `originalValue` was on first mount, and
	// submitting it would silently overwrite the real (now-stale) policy with old data.
	useEffect(() => {
		form.setFieldValue('json', originalValue)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [originalValue])

	return (
		<div className='px-6 pb-6'>
			<form.AppForm>
				<Text size='small' weight='plus' leading='compact' className='mb-2 text-ui-fg-subtle'>
					{L.Settings.DeviceStatus.Gms.PolicyOverridesLabel()}
				</Text>
				<Text size='small' className='mb-2 text-ui-fg-muted'>
					{L.Settings.DeviceStatus.Gms.PolicyOverridesHint()}
				</Text>
				<form.AppField
					name='json'
					children={field => (
						<Textarea
							className='font-mono text-xs'
							rows={10}
							value={field.state.value}
							onChange={e => field.handleChange(e.target.value)}
						/>
					)}
				/>
				<form.Subscribe selector={state => ({ json: state.values.json, isSubmitting: state.isSubmitting })}>
					{({ json, isSubmitting }) => (
						<div className='mt-2 flex justify-end gap-x-2'>
							<Button
								size='small'
								variant='secondary'
								disabled={json === originalValue}
								onClick={() => form.setFieldValue('json', originalValue)}
							>
								{L.Settings.DeviceStatus.Gms.ResetOverridesButton()}
							</Button>
							<Button
								size='small'
								disabled={json === originalValue}
								isLoading={isSubmitting}
								onClick={() => form.handleSubmit()}
							>
								{L.Settings.DeviceStatus.Gms.SaveOverridesButton()}
							</Button>
						</div>
					)}
				</form.Subscribe>
			</form.AppForm>
		</div>
	)
}

type AmapiStatus = NonNullable<ReturnType<typeof useGetPointOfSaleDeviceStatus>['data']>['amapi']

function GmsSection({
	pointOfSaleUuid,
	pointOfSaleName,
	amapi
}: {
	pointOfSaleUuid: string
	pointOfSaleName: string
	amapi: AmapiStatus
}) {
	const { L } = useI18n()
	const me = useGetMe()
	const isDeveloper = me.data?.role === UserRole.Developer
	const prompt = usePrompt()
	const [enrollOpen, setEnrollOpen] = useState(false)
	const rebootDevice = useRebootPointOfSaleGmsDevice(true)
	const unenrollDevice = useDeletePointOfSaleGmsDevice(true)

	const isGms = amapi?.mdmProvider === 'GMS'
	const hasDevice = isGms && !!amapi?.deviceName

	const handleReboot = async () => {
		const confirmed = await prompt({
			title: L.Settings.DeviceStatus.Gms.RebootConfirmTitle(),
			description: L.Settings.DeviceStatus.Gms.RebootConfirmDescription(),
			verificationText: pointOfSaleName
		})
		if (!confirmed) {
			return
		}
		const result = await rebootDevice.mutateAsync({ path: { pointOfSaleUuid } })
		if (!result.isSuccess) {
			return
		}
		toast.success(L.Settings.DeviceStatus.Gms.RebootSuccess())
	}

	const handleUnenroll = async () => {
		const confirmed = await prompt({
			variant: 'danger',
			title: L.Settings.DeviceStatus.Gms.UnenrollConfirmTitle(),
			description: L.Settings.DeviceStatus.Gms.UnenrollConfirmDescription(),
			verificationText: pointOfSaleName
		})
		if (!confirmed) {
			return
		}
		const result = await unenrollDevice.mutateAsync({ path: { pointOfSaleUuid } })
		if (!result.isSuccess) {
			return
		}
		toast.success(L.Settings.DeviceStatus.Gms.UnenrollSuccess())
	}

	return (
		<>
			<ContainerHeader
				heading={L.Settings.DeviceStatus.Gms.Title()}
				rightSide={
					<Button size='small' variant='secondary' onClick={() => setEnrollOpen(true)}>
						{L.Settings.DeviceStatus.Gms.EnrollButton()}
					</Button>
				}
			/>

			{!isGms ? (
				<div className='px-6 pb-6'>
					<Text size='small' className='text-ui-fg-subtle'>
						{L.Settings.DeviceStatus.Gms.NotEnrolled()}
					</Text>
				</div>
			) : !hasDevice ? (
				<div className='px-6 pb-6'>
					<Text size='small' className='text-ui-fg-subtle'>
						{L.Settings.DeviceStatus.Gms.NoBoundDevice()}
					</Text>
				</div>
			) : (
				<>
					<SectionRow title={L.Settings.DeviceStatus.Gms.DeviceName()} value={amapi.deviceName} />
					<SectionRow title={L.Settings.DeviceStatus.Gms.State()} value={amapi.state} />
					<SectionRow title={L.Settings.DeviceStatus.Gms.AppliedState()} value={amapi.appliedState} />
					<SectionRow
						title={L.Settings.DeviceStatus.Gms.PolicyCompliant()}
						value={
							<Badge color={amapi.policyCompliant ? 'green' : 'red'} size='2xsmall'>
								{amapi.policyCompliant
									? L.Settings.DeviceStatus.Gms.Yes()
									: L.Settings.DeviceStatus.Gms.No()}
							</Badge>
						}
					/>
					{!!amapi.nonComplianceReasons?.length && (
						<SectionRow
							title={L.Settings.DeviceStatus.Gms.NonComplianceReasons()}
							value={
								<div className='flex flex-wrap gap-1.5'>
									{amapi.nonComplianceReasons.map(reason => (
										<Badge key={reason} color='red' size='2xsmall'>
											{reason}
										</Badge>
									))}
								</div>
							}
						/>
					)}
					<SectionRow
						title={L.Settings.DeviceStatus.Gms.LastPolicySync()}
						value={
							amapi.lastPolicySyncTime
								? L.Date.DateAndTime(new Date(amapi.lastPolicySyncTime).getTime())
								: undefined
						}
					/>
					<SectionRow
						title={L.Settings.DeviceStatus.Gms.InstalledVersionCode()}
						value={amapi.installedVersionCode ?? undefined}
					/>
					<SectionRow title={L.Settings.DeviceStatus.Gms.HardwareModel()} value={amapi.hardwareModel} />
					<SectionRow title={L.Settings.DeviceStatus.Gms.AndroidVersion()} value={amapi.androidVersion} />
					<GmsMinimumVersionRow
						pointOfSaleUuid={pointOfSaleUuid}
						minimumVersionCode={amapi.minimumVersionCode}
					/>

					<div className='flex items-center gap-x-2 px-6 pb-6'>
						<Button
							size='small'
							variant='secondary'
							isLoading={rebootDevice.isPending}
							onClick={handleReboot}
						>
							{L.Settings.DeviceStatus.Gms.RebootButton()}
						</Button>
						{isDeveloper && (
							<Button
								size='small'
								variant='danger'
								isLoading={unenrollDevice.isPending}
								onClick={handleUnenroll}
							>
								{L.Settings.DeviceStatus.Gms.UnenrollButton()}
							</Button>
						)}
					</div>

					{isDeveloper && (
						<GmsPolicyOverridesEditor
							pointOfSaleUuid={pointOfSaleUuid}
							policyOverrides={(amapi as { policyOverrides?: unknown }).policyOverrides}
						/>
					)}
				</>
			)}

			<GmsEnrollDrawer pointOfSaleUuid={pointOfSaleUuid} open={enrollOpen} onOpenChange={setEnrollOpen} />
		</>
	)
}

export function DeviceStatusPanel({
	pointOfSaleUuid,
	pointOfSaleName
}: {
	pointOfSaleUuid: string
	pointOfSaleName: string
}) {
	const { L } = useI18n()
	const prompt = usePrompt()
	const me = useGetMe()
	const canLockKiosk = isAdminOrDeveloperRole(me.data?.role)
	const [pushUpdateOpen, setPushUpdateOpen] = useState(false)

	const deviceStatus = useGetPointOfSaleDeviceStatus(
		{ pointOfSaleUuid },
		{ refetchInterval: DEVICE_STATUS_REFETCH_INTERVAL_MS }
	)
	const refreshStatus = useRefreshPointOfSaleDeviceStatus(true)
	const restartApp = useRestartPointOfSaleApp(true)
	const lockKiosk = useLockPointOfSaleKiosk(true)

	const handleRefresh = async () => {
		const result = await refreshStatus.mutateAsync({ path: { pointOfSaleUuid } })
		if (result.isSuccess) {
			toast.success(L.Settings.DeviceStatus.RefreshSuccess())
		} else {
			toast.error(L.ApiErrorAlert.FailedToLoadData())
		}
	}

	const handleRestart = async () => {
		const confirmed = await prompt({
			title: L.Settings.DeviceStatus.RestartAppConfirmTitle(),
			description: L.Settings.DeviceStatus.RestartAppConfirmDescription(),
			verificationText: pointOfSaleName
		})
		if (!confirmed) {
			return
		}
		const result = await restartApp.mutateAsync({ path: { pointOfSaleUuid } })
		if (!result.isSuccess) {
			return
		}
		toast.success(L.Settings.DeviceStatus.RestartAppSuccess())
	}

	const handleLockKiosk = async () => {
		const confirmed = await prompt({
			title: L.Settings.DeviceStatus.LockKioskConfirmTitle(),
			description: L.Settings.DeviceStatus.LockKioskConfirmDescription(),
			verificationText: pointOfSaleName
		})
		if (!confirmed) {
			return
		}
		const result = await lockKiosk.mutateAsync({ path: { pointOfSaleUuid } })
		if (!result.isSuccess) {
			if (isDeviceOffline(result.response)) {
				toast.error(L.Settings.DeviceStatus.LockKioskOffline())
				return
			}
			toast.error(L.Error.SomethingWentWrong())
			return
		}
		toast.success(L.Settings.DeviceStatus.LockKioskSuccess())
	}

	const status = deviceStatus.data?.status
	const online = deviceStatus.data?.online ?? false
	const enrollment = deviceStatus.data?.enrollment

	return (
		<>
			<Container>
				<EnrollmentSection
					pointOfSaleUuid={pointOfSaleUuid}
					pointOfSaleName={pointOfSaleName}
					enrollment={enrollment}
				/>
			</Container>

			<Container>
				<GmsSection
					pointOfSaleUuid={pointOfSaleUuid}
					pointOfSaleName={pointOfSaleName}
					amapi={deviceStatus.data?.amapi}
				/>
			</Container>
			{!!enrollment && (
				<Container>
					<ContainerHeader
						heading={L.Settings.DeviceStatus.Title()}
						rightSide={
							<div className='flex items-center gap-x-2'>
								<Button
									variant='secondary'
									size='small'
									onClick={handleRefresh}
									isLoading={refreshStatus.isPending}
								>
									{L.Settings.DeviceStatus.RefreshButton()}
								</Button>
								<Button
									variant='secondary'
									size='small'
									onClick={handleRestart}
									isLoading={restartApp.isPending}
								>
									{L.Settings.DeviceStatus.RestartAppButton()}
								</Button>
								{canLockKiosk && (
									<Button
										variant='secondary'
										size='small'
										onClick={handleLockKiosk}
										isLoading={lockKiosk.isPending}
									>
										{L.Settings.DeviceStatus.LockKioskButton()}
									</Button>
								)}
								<Button size='small' onClick={() => setPushUpdateOpen(true)}>
									{L.Settings.DeviceStatus.PushUpdateButton()}
								</Button>
							</div>
						}
					/>

					{!status ? (
						<EmptyState
							title={L.Settings.DeviceStatus.Title()}
							description={L.Settings.DeviceStatus.NeverReported()}
							className='min-h-[120px]'
						/>
					) : (
						<>
							<SectionRow
								title={L.Settings.DeviceStatus.Title()}
								value={
									<Badge color={online ? 'green' : 'grey'} size='2xsmall'>
										{online ? L.Settings.DeviceStatus.Online() : L.Settings.DeviceStatus.Offline()}
									</Badge>
								}
							/>
							<SectionRow
								title={L.Settings.DeviceStatus.ReportedAt()}
								value={L.Date.DateAndTime(status.reportedAt)}
							/>
							<SectionRow title={L.Settings.DeviceStatus.AppVersion()} value={status.appVersion} />
							<SectionRow
								title={L.Settings.DeviceStatus.DisplayRuntimeVersion()}
								value={status.displayRuntimeVersion}
							/>
							<SectionRow
								title={L.Settings.DeviceStatus.ConfiguredRuntimeVersion()}
								value={status.configuredRuntimeVersion}
							/>
							<SectionRow
								title={L.Settings.DeviceStatus.Platform()}
								value={[status.platform, status.platformVersion].filter(Boolean).join(' ')}
							/>
							<SectionRow title={L.Settings.DeviceStatus.DeviceModel()} value={status.deviceModel} />
							<SectionRow title={L.Settings.DeviceStatus.NetworkType()} value={status.networkType} />
							<SectionRow
								title={L.Settings.DeviceStatus.OperatingMode()}
								value={status.appState?.operatingMode}
							/>
							<SectionRow
								title={L.Settings.DeviceStatus.UpdateChannel()}
								value={status.update?.channel}
							/>
							<SectionRow title={L.Settings.DeviceStatus.UpdateId()} value={status.update?.updateId} />
							<SectionRow
								title={L.Settings.DeviceStatus.UpdateCreatedAt()}
								value={
									status.update?.createdAt
										? L.Date.DateAndTime(new Date(status.update.createdAt))
										: undefined
								}
							/>
							{status.update?.updatePending && (
								<SectionRow
									title={L.Settings.DeviceStatus.UpdatePending({
										time: status.update.pendingSince
											? L.Date.DateAndTime(status.update.pendingSince)
											: L.Settings.DeviceStatus.Unknown()
									})}
								/>
							)}
							<SectionRow
								title={L.Settings.DeviceStatus.Health()}
								value={
									<div className='flex flex-wrap gap-1.5'>
										<HealthBadge
											label={L.Settings.DeviceStatus.PrinterConnected()}
											connected={status.health?.printerConnected}
										/>
										<HealthBadge
											label={L.Settings.DeviceStatus.ScannerConnected()}
											connected={status.health?.scannerConnected}
										/>
										<HealthBadge
											label={L.Settings.DeviceStatus.PaymentTerminalReachable()}
											connected={status.health?.paymentTerminalReachable}
										/>
									</div>
								}
							/>
							<div className='px-6 pb-6'>
								<Text size='small' weight='plus' leading='compact' className='mb-2 text-ui-fg-subtle'>
									{L.Settings.DeviceStatus.Environment()}
								</Text>
								<pre className='max-h-[320px] overflow-auto rounded-md border border-ui-border-base bg-ui-bg-subtle p-3 text-xs text-ui-fg-subtle'>
									{JSON.stringify(status.env, null, 2)}
								</pre>
							</div>
						</>
					)}

					<PushUpdateDrawer
						pointOfSaleUuid={pointOfSaleUuid}
						open={pushUpdateOpen}
						onOpenChange={setPushUpdateOpen}
					/>
				</Container>
			)}
		</>
	)
}
